import { useState } from 'react';

import { notify } from '@shared';
import { useConfirm } from './useConfirm';
import { getErrorMessage } from '@utilities';
import { useModal, useRequest } from '@hooks';
import type { UseConfirmParams } from './useConfirm';

type UseAddEditParams<ItemType extends {}, AddType, EditType> = {
  name: string;
  onAdd?: (payload: AddType) => void;
  onEdit?: (id: string, payload: EditType) => void;
  onDelete?: (id: string) => void;
  onRefresh: () => void;

  nameKey?: keyof ItemType & string;
  deleteConfig?: UseConfirmParams & {
    /**
     * function fully renders delete window text
     */
    getText?: (item: ItemType) => React.ReactNode;
    /**
     * function defines text of current item, no all sentence
     */
    getItemLabel?: (item: ItemType) => string;
  };
};

const getDeleteText = <
  ItemType extends { id: string; name?: string },
  AddType,
  EditType,
>(
  item: ItemType,
  { nameKey, deleteConfig }: UseAddEditParams<ItemType, AddType, EditType>
) => {
  const { getText } = deleteConfig || {};
  if (getText) return getText(item);

  const { getItemLabel } = deleteConfig || {};

  const itemName = nameKey ? item[nameKey] : item.name || item.id || '';
  const label = getItemLabel ? getItemLabel(item) : `"${itemName}"`;

  return [
    `Are you sure you want to delete ${label}?`,
    `This action cannot be undone.`,
  ].join('\n');
};

export const useActions = <
  ItemType extends { id: string; name?: string },
  AddType,
  EditType,
>(
  config: UseAddEditParams<ItemType, AddType, EditType>
) => {
  const {
    name,
    onAdd,
    onEdit,
    onDelete,
    onRefresh,

    deleteConfig,
  } = config;

  const [item, setItem] = useState<ItemType>();

  const { isOpened, onOpen, onClose } = useModal();
  const handleClose = () => {
    setItem(undefined);
    onClose();
  };
  const [performAdd, adding] = useRequest(
    (_signal, payload: AddType) => onAdd?.(payload),
    {
      deps: [name],
      onFinally: onRefresh,
      onError: ex => {
        return notify.error(getErrorMessage(ex, `Failed to save ${name}!`));
      },
      onSuccess: () => {
        notify.success(`${name} added!`);
        handleClose();
      },
    }
  );

  const handleStartAdd = () => {
    setItem(undefined);
    onOpen();
  };
  const handleAdd = (payload: Partial<AddType>) => {
    performAdd(payload as AddType);
  };

  const [performUpdate, editing] = useRequest(
    (_signal, payload: EditType) => onEdit?.(item?.id!, payload),
    {
      deps: [item?.id, name],
      onFinally: onRefresh,
      onError: ex =>
        notify.error(getErrorMessage(ex, `Failed to update ${name}!`)),
      onSuccess: () => {
        notify.success(`${name} updated!`);
        handleClose();
      },
    }
  );
  const handleStartEdit = (item: ItemType) => {
    setItem(item);
    onOpen();
  };
  const handleEdit = (payload: Partial<EditType>) => {
    performUpdate(payload as EditType);
  };

  const [performDelete, deleting] = useRequest(
    (_signal, id: string) => onDelete?.(id),
    {
      deps: [name],
      onFinally: onRefresh,
      onError: ex =>
        notify.error(getErrorMessage(ex, `Failed to delete ${name}!`)),
      onSuccess: () => {
        notify.success(`${name} deleted!`);
      },
    }
  );
  const { element, open } = useConfirm(deleteConfig);
  const handleStartDelete = (item: ItemType) => {
    const text = getDeleteText<ItemType, AddType, EditType>(item, config);

    open({
      text: text,
      title: `Delete ${name}`,
      confirmText: 'Delete',
      confirmType: 'danger',
      ...deleteConfig,
      onConfirm: () => performDelete(item.id),
    });
  };

  const updating = adding || editing || deleting;

  return {
    element,

    isOpened,
    updating,
    initialValues: item,

    handleAdd,
    handleStartAdd,

    handleEdit,
    handleStartEdit,

    handleClose,
    handleStartDelete,
  };
};
